import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setAccessToken, clearAccessToken } from './tokenService';
import {
  checkCandidateSession,
  candidateLogin,
  candidateLogout,
  candidateSetPassword,
  initiateSSO,
  logout as logoutApi,
} from './authApi';
import { handleRedirectResult, logoutSSO } from './msalService';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasActiveExam, setHasActiveExam] = useState(false);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Check MSAL redirect result (Azure AD redirects back after login)
        const ssoUser = await handleRedirectResult();
        if (ssoUser) {
          setUser(ssoUser);
          sessionStorage.setItem('commscoach_user', JSON.stringify(ssoUser));
          setIsLoading(false);
          return;
        }

        // Check for stored session in sessionStorage
        const storedUser = sessionStorage.getItem('commscoach_user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } catch {
        // No valid session
      } finally {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, []);

  // Candidate login: Redis first -> Cognito fallback
  const loginCandidate = useCallback(async (email, password) => {
    setError(null);

    try {
      // Step 1: Check Redis for existing valid session
      const sessionCheck = await checkCandidateSession(email);

      // Check if user is blocked/deactivated
      if (sessionCheck.blocked) {
        const errMsg = sessionCheck.reason || 'Your account has been deactivated.';
        setError(errMsg);
        throw new Error(errMsg);
      }

      if (sessionCheck.valid && sessionCheck.user) {
        // Valid session found in Redis - restore it
        const restoredUser = {
          id: sessionCheck.user.id,
          username: sessionCheck.user.email.split('@')[0],
          email: sessionCheck.user.email,
          full_name: sessionCheck.user.full_name,
          role: sessionCheck.user.role,
        };
        // Store token if returned by session check
        if (sessionCheck.token) {
          setAccessToken(sessionCheck.token);
        }
        setUser(restoredUser);
        setHasActiveExam(sessionCheck.has_active_exam);
        sessionStorage.setItem('commscoach_user', JSON.stringify(restoredUser));
        return {};
      }

      // Step 2: Redis miss -> authenticate with Cognito
      const result = await candidateLogin(email, password);

      if (result.success && result.user && result.token) {
        // Cognito success - JWT stored in Redis by backend
        const authenticatedUser = {
          id: result.user.id,
          username: result.user.email.split('@')[0],
          email: result.user.email,
          full_name: result.user.full_name,
          role: result.user.role,
        };
        setAccessToken(result.token);
        setUser(authenticatedUser);
        sessionStorage.setItem('commscoach_user', JSON.stringify(authenticatedUser));
        return {};
      }

      if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        return { challenge: result.challenge, session: result.session || '' };
      }

      // Auth failed
      const errMsg = result.error || 'Invalid credentials';
      setError(errMsg);
      throw new Error(errMsg);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  }, []);

  // Handle NEW_PASSWORD_REQUIRED challenge
  const setCandidatePassword = useCallback(async (username, newPassword, session) => {
    setError(null);

    try {
      const result = await candidateSetPassword(username, newPassword, session);

      if (result.success && result.user && result.token) {
        const authenticatedUser = {
          id: result.user.id,
          username: result.user.email.split('@')[0],
          email: result.user.email,
          full_name: result.user.full_name,
          role: result.user.role,
        };
        setAccessToken(result.token);
        setUser(authenticatedUser);
        sessionStorage.setItem('commscoach_user', JSON.stringify(authenticatedUser));
      } else {
        const errMsg = result.error || 'Failed to set password';
        setError(errMsg);
        throw new Error(errMsg);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set password';
      setError(message);
      throw err;
    }
  }, []);

  const loginSSO = useCallback(() => {
    initiateSSO();
  }, []);

  const logout = useCallback(async () => {
    clearAccessToken();
    setUser(null);
    setHasActiveExam(false);
    sessionStorage.removeItem('commscoach_user');
    // If SSO user, also logout from Azure AD
    try {
      await logoutSSO();
    } catch {
      // Ignore — candidate users won't have MSAL session
    }
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login: loginCandidate,
    loginCandidate,
    setCandidatePassword,
    loginSSO,
    logout,
    error,
    hasActiveExam,
    setUserDirect: (userData) => {
      setUser(userData);
      sessionStorage.setItem('commscoach_user', JSON.stringify(userData));
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
